import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import logError from '@alltrails/core/utils/logError';
import { useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { LatLng } from '../../types/Geo';
import { useAppDispatch, useAppSelector } from '../../redux';
import { SegmentMetadata, useLazyGetMetadataQuery } from '../../redux/customRoutesApi';
import { selectCustomRouteSegmentsPaths, updateMetadata, setToastMessage } from '../../redux/customRoute';
import { getGeneralError } from '../../utils/customRoutes/constants';

const INTERVAL = 500;

export type SegmentsPathsMap = Record<number, LatLng[][]>;
type UseMetadataParams = {
  autoUpdate?: boolean;
  trackUserInteractions?: () => void;
};

/**
 * Build a map of segment indices to their paths, filtering out segments with invalid paths
 */
const buildSegmentsPathsMap = (segmentsPaths: ReturnType<typeof selectCustomRouteSegmentsPaths>): SegmentsPathsMap => {
  return segmentsPaths.reduce<SegmentsPathsMap>((acc, segmentPaths, index) => {
    if (segmentPaths.length > 0 && segmentPaths[0].points.length > 1) {
      acc[index] = segmentPaths.map(segmentPath => segmentPath.points);
    }
    return acc;
  }, {});
};

/**
 * If autoUpdate is true listen for changes to route segments and debounce fetching metadata.
 * Note that autoUpdate can only be true once within a given app to prevent duplicative metadata updates.
 *
 * We do this in an effect rather than in thunks or a redux middleware so that we:
 * - Don't have to manually fetch metadata as part of every action that updates segments
 * - Can debounce metadata updates across all routing actions that update segments
 * - Can use RTK query to cache metadata by activity and segments
 * - Can fetch metadata after undo and redo actions if the state we restore did not have metadata fetched originally
 *   (e.g. restoring segment state that occurred during a flurry of segment updates for which metadata was never fetched due to debouncing)
 */
const useMetadata = ({ autoUpdate, trackUserInteractions }: UseMetadataParams = {}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const activity = useAppSelector(state => state.customRoute.route.activityType);
  const segments = useAppSelector(state => state.customRoute.route.segments);
  // Note that we only select segment paths so that updating segment metadata doesn't trigger infinite metadata fetches
  const segmentsPaths = useAppSelector(selectCustomRouteSegmentsPaths);
  const user = useUser();

  // This is a ref instead of state to avoid triggering a rerender when initialized
  const initializedRef = useRef(false);

  const [trigger] = useLazyGetMetadataQuery();
  const fetch = useCallback(
    async (activity: string, segmentsPathsMap: SegmentsPathsMap) => {
      const { isSuccess, isError, data, error } = await trigger(
        {
          activityUid: activity,
          segmentsPaths: Object.values(segmentsPathsMap)
        },
        true // preferCacheValue
      );

      trackUserInteractions?.();

      if (isError) {
        dispatch(setToastMessage(getGeneralError(intl)));
        logError(new Error(`Failed to fetch metadata for activity ${activity}`, { cause: error }));
      }

      return { isSuccess, data };
    },
    [trigger, dispatch, intl, trackUserInteractions]
  );

  const fetchAndUpdate = useCallback(
    async (activity: string, segmentsPathsMap: SegmentsPathsMap) => {
      if (!user) {
        return;
      }
      const { isSuccess, data } = await fetch(activity, segmentsPathsMap);

      if (isSuccess && data) {
        // Only update metadata for segments that were fetched
        const segmentsMetadata = Object.keys(segmentsPathsMap).reduce(
          (acc, segmentIndex, dataIndex) => {
            acc[segmentIndex] = data.segmentsMetadata[dataIndex];
            return acc;
          },
          {} as Record<number, SegmentMetadata>
        );
        dispatch(updateMetadata({ ...data, segmentsMetadata }));
      }
    },
    [fetch, dispatch, user]
  );

  // Fire the first fetch immediately so that a single action feels responsive and then debounce subsequent fetches to prevent retrieving metadata that will never be used
  // Note that the dependencies of this memo must be stable to prevent resetting the debounce timer
  const debouncedFetchAndUpdate = useMemo(() => debounce(fetchAndUpdate, INTERVAL, { leading: true, trailing: true }), [fetchAndUpdate]);

  // If autoUpdate is enabled fetch metadata when a change is detected
  useEffect(() => {
    if (!autoUpdate) {
      return;
    }

    if (!initializedRef.current) {
      initializedRef.current = true;
      // On initial render, check if segments need metadata (viewing mode without startLocation, needed for flyover)
      const needsMetadata = !!user && segments.length > 0 && segments.some(segment => !segment.startLocation);
      if (needsMetadata && activity) {
        const segmentsPathsMap = buildSegmentsPathsMap(segmentsPaths);
        if (Object.values(segmentsPathsMap).length > 0) {
          fetchAndUpdate(activity, segmentsPathsMap);
        }
      }

      return;
    }

    // On subsequent renders (editing mode), build the paths map to track changes
    const segmentsPathsMap = buildSegmentsPathsMap(segmentsPaths);

    if (Object.values(segmentsPathsMap).length === 0) {
      // On new custom route pages, track the first interaction
      trackUserInteractions?.();
      return;
    }
    if (activity) {
      debouncedFetchAndUpdate(activity, segmentsPathsMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- segments is only used in initial render check, intentionally excluded to prevent feedback loop with updateMetadata action
  }, [autoUpdate, initializedRef, activity, segmentsPaths, debouncedFetchAndUpdate, fetchAndUpdate, dispatch, trackUserInteractions, user]);

  return fetch;
};

export default useMetadata;
